import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { Navigation } from "./navigation";
import { Contact } from "./contact";
import services from "../data/services";
import JsonData from "../data/data.json";

const BASE_URL = "https://cyberelectrix.co.nz";

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    setContactData(JsonData.Contact);
  }, []);

  // Scroll to top when navigating to a service page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  const service = services.find((s) => s.slug === serviceSlug);

  // Unknown slug — redirect to homepage
  if (!service) {
    return <Navigate to="/" replace />;
  }

  const canonicalUrl = `${BASE_URL}/services/${service.slug}`;

  return (
    <div>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="keywords" content={(service.keywords || []).join(", ")} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CyberElectrix" />
        <meta property="og:image" content={`${BASE_URL}/img/og-image.jpg`} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={service.metaTitle} />
        <meta name="twitter:description" content={service.metaDescription} />
        <meta name="twitter:image" content={`${BASE_URL}/img/og-image.jpg`} />

      </Helmet>

      {/* Site navigation */}
      <Navigation />

      {/* Hero section */}
      <div
        id="service-hero"
        style={{
          background: "linear-gradient(to right, #181b2e 0%, #2a1a3e 50%, #181b2e 100%)",
          padding: "120px 0 60px",
          color: "#fff",
          textAlign: "center"
        }}
      >
        <div className="container">
          <i
            className={service.icon}
            style={{ fontSize: "48px", color: "#f8825a", marginBottom: "20px", display: "block" }}
            aria-hidden="true"
          ></i>
          <h1 style={{ fontSize: "36px", fontWeight: "700", marginBottom: "16px" }}>
            {service.name}
          </h1>
          <p style={{ fontSize: "18px", color: "#ccc", maxWidth: "700px", margin: "0 auto" }}>
            {service.headline}
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ background: "#f5f5f5", padding: "12px 0", borderBottom: "1px solid #e0e0e0" }}>
        <div className="container">
          <ol className="breadcrumb" style={{ background: "transparent", margin: 0, padding: 0, fontSize: "14px" }}>
            <li>
              <Link to="/" style={{ color: "#f8825a" }}>Home</Link>
            </li>
            <li className="active">{service.name}</li>
          </ol>
        </div>
      </div>

      {/* What we do */}
      <div style={{ padding: "60px 0" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <div className="section-title text-center" style={{ marginBottom: "40px" }}>
                <h2>What We Do</h2>
              </div>
              <p style={{ fontSize: "16px", lineHeight: "1.8", color: "#555", textAlign: "center" }}>
                {service.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits / Features list */}
      <div style={{ background: "#f9f9f9", padding: "60px 0" }}>
        <div className="container">
          <div className="section-title text-center" style={{ marginBottom: "40px" }}>
            <h2>What's Included</h2>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {service.benefits.map((benefit, index) => (
                  <li
                    key={index}
                    style={{
                      padding: "12px 0 12px 36px",
                      position: "relative",
                      fontSize: "15px",
                      color: "#444",
                      borderBottom: index < service.benefits.length - 1 ? "1px solid #eee" : "none"
                    }}
                  >
                    <i
                      className="fa fa-check"
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "14px",
                        color: "#f8825a",
                        fontSize: "16px"
                      }}
                      aria-hidden="true"
                    ></i>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ section */}
      {service.faqs && service.faqs.length > 0 && (
        <div style={{ padding: "60px 0" }}>
          <div className="container">
            <div className="section-title text-center" style={{ marginBottom: "40px" }}>
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className="row">
              <div className="col-md-8 col-md-offset-2">
                <div className="panel-group" id="faq-accordion" role="tablist" aria-multiselectable="true">
                  {service.faqs.map((faq, index) => {
                    const collapseId = `faq-collapse-${index}`;
                    const headingId = `faq-heading-${index}`;
                    return (
                      <div
                        className="panel panel-default"
                        key={index}
                        style={{ border: "1px solid #e5e5e5", borderRadius: "4px", marginBottom: "8px", boxShadow: "none" }}
                      >
                        <div className="panel-heading" role="tab" id={headingId} style={{ background: "#fff", borderBottom: "none", padding: 0 }}>
                          <h3 className="panel-title" style={{ margin: 0 }}>
                            <a
                              role="button"
                              data-toggle="collapse"
                              data-parent="#faq-accordion"
                              href={`#${collapseId}`}
                              aria-expanded={index === 0 ? "true" : "false"}
                              aria-controls={collapseId}
                              style={{ display: "block", padding: "14px 20px", color: "#333", fontWeight: "600", fontSize: "15px", textDecoration: "none", borderLeft: "3px solid #f8825a" }}
                            >
                              {faq.question}
                            </a>
                          </h3>
                        </div>
                        <div
                          id={collapseId}
                          className={`panel-collapse collapse${index === 0 ? " in" : ""}`}
                          role="tabpanel"
                          aria-labelledby={headingId}
                        >
                          <div className="panel-body" style={{ padding: "14px 20px 14px 23px", fontSize: "15px", color: "#555", lineHeight: "1.7", borderTop: "1px solid #f0f0f0" }}>
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div
        style={{
          background: "linear-gradient(to right, #181b2e 0%, #2a1a3e 100%)",
          padding: "60px 0",
          textAlign: "center",
          color: "#fff"
        }}
      >
        <div className="container">
          <h2 style={{ marginBottom: "20px" }}>Ready to Get Started?</h2>
          <p style={{ fontSize: "16px", color: "#ccc", marginBottom: "32px", maxWidth: "600px", margin: "0 auto 32px" }}>
            Contact our team of licensed Auckland electricians today for a free quote or to discuss your requirements.
          </p>
          <div>
            <a
              href="https://cyberelectrix.co.nz/#contact"
              className="btn btn-custom btn-lg"
              style={{ marginRight: "16px", marginBottom: "12px" }}
            >
              Get a Quote
            </a>
            <a
              href="tel:+64275733478"
              className="btn btn-lg"
              style={{
                background: "transparent",
                border: "2px solid #f8825a",
                color: "#f8825a",
                marginBottom: "12px"
              }}
            >
              <i className="fa fa-phone" style={{ marginRight: "8px" }} aria-hidden="true"></i>
              Call Now: 027 573 3478
            </a>
          </div>
          <div style={{ marginTop: "24px" }}>
            <Link
              to="/"
              style={{ color: "#aaa", fontSize: "14px", textDecoration: "none" }}
            >
              <i className="fa fa-arrow-left" style={{ marginRight: "6px" }} aria-hidden="true"></i>
              Back to all services
            </Link>
          </div>
        </div>
      </div>

      {/* Related Services */}
      <div style={{ padding: "60px 0", background: "#f9f9f9" }}>
        <div className="container">
          <div className="section-title text-center" style={{ marginBottom: "40px" }}>
            <h2>Our Other Services</h2>
          </div>
          <div className="row">
            {services
              .filter((s) => s.slug !== serviceSlug)
              .map((related) => (
                <div key={related.slug} className="col-md-4 col-sm-6 col-xs-12" style={{ marginBottom: "24px" }}>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "24px 20px",
                      background: "#fff",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <div style={{ marginBottom: "12px" }}>
                      <i
                        className={related.icon}
                        style={{ fontSize: "28px", color: "#f8825a" }}
                        aria-hidden="true"
                      ></i>
                    </div>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#222", marginBottom: "8px", marginTop: 0 }}>
                      {related.name}
                    </h3>
                    <p style={{ fontSize: "14px", color: "#666", lineHeight: "1.6", flexGrow: 1, marginBottom: "16px" }}>
                      {related.description.length > 80
                        ? related.description.slice(0, 80).trimEnd() + "…"
                        : related.description}
                    </p>
                    <Link
                      to={`/services/${related.slug}`}
                      style={{ color: "#f8825a", fontSize: "14px", fontWeight: "600", textDecoration: "none" }}
                    >
                      Learn more &rarr;
                    </Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Contact form at the bottom */}
      <Contact data={contactData} />
    </div>
  );
};

export default ServicePage;
